import { client } from '@/_shared/lib/rpc';

/**
 * The invite checks of Lookout's sign-up pages. config-backend honours the invite marker, which asks for an account
 * with no RudderStack organization, only while Lookout's grant for the invite is live (ACT2-887).
 */

const INVITE_CHECK_FAILED = 'Could not check your invite. Please try again.';
// config-backend's message, and the web app's, for an invite that is no longer valid when its account is created.
export const INVITE_EXPIRED = 'Invitation expired. Please ask your admin to invite you again.';
// config-backend also refuses an invitee link from before ACT2-887, which names no invite. The invite itself can still
// be pending, and its link starts a sign-up that names it.
export const INVITEE_LINK_EXPIRED =
  'This sign-up link no longer works. Open your invitation email and select Accept invitation to sign up again.';

// Lookout's invite ids, as in `/invite/<id>`.
const INVITE_ID = '[A-Za-z0-9-]{1,64}';
const INVITE_ID_PATTERN = new RegExp(`^${INVITE_ID}$`);
const INVITE_PATH_PATTERN = new RegExp(`^/invite/(${INVITE_ID})(?:[?#]|$)`);

/** The invite check failed, or the invite is no longer valid. A new sign-up link does not change either. */
export class InviteCheckError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InviteCheckError';
  }
}

/** The invite id, or null when `value` is not one. */
export function inviteIdOf(value: string | null): string | null {
  return value && INVITE_ID_PATTERN.test(value) ? value : null;
}

/** The invite that a return path names. The invite page sends `/invite/<id>`. */
export function inviteIdOfReturnTo(returnTo: string | null): string | null {
  if (!returnTo) return null;
  return INVITE_PATH_PATTERN.exec(returnTo)?.[1] ?? null;
}

/** The sign-up page. With `invite`, the page treats the user as an invitee of the invite that `returnTo` names. */
export function signupPath({ invite, returnTo }: { invite: boolean; returnTo: string | null }): string {
  const query = new URLSearchParams();
  if (invite) query.set('invite', '1');
  if (returnTo) query.set('return_to', returnTo);
  const search = query.toString();
  return search ? `/signup?${search}` : '/signup';
}

/** The sign-up page of an invite, so that the new sign-up link keeps the invite (ACT2-894). */
export function inviteSignupPath(inviteId: string): string {
  return signupPath({ invite: true, returnTo: `/invite/${inviteId}` });
}

/** The login page, which opens the invite after the sign-in. Without an invite, the plain login page. */
export function inviteLoginPath(inviteId: string | null): string {
  return inviteId ? `/login?${new URLSearchParams({ return_to: `/invite/${inviteId}` }).toString()}` : '/login';
}

/**
 * The sign-up page asks before it sends the marker. Only a pending invite for this email earns it: a link with
 * `?invite=1` alone does not. When the invite matches, Lookout's server grants it.
 */
export async function isInvitee(inviteId: string | null, email: string): Promise<boolean> {
  if (!inviteId) return false;
  try {
    return (await client.auth.signupInvite({ inviteId, email })).invitee;
  } catch {
    throw new InviteCheckError(INVITE_CHECK_FAILED);
  }
}

/**
 * The completion page asks just before config-backend creates the account. The emailed link can be 72 hours old, so
 * Lookout's server grants the invite again. A dead invite stops the sign-up, as a dead web-app invitation does. For an
 * accepted invite the account exists, and `/signup` says so.
 */
export async function renewInvite(inviteId: string): Promise<void> {
  let grant: 'granted' | 'accepted' | 'no_invite';
  try {
    ({ grant } = await client.auth.renewSignupInvite({ inviteId }));
  } catch {
    throw new InviteCheckError(INVITE_CHECK_FAILED);
  }
  if (grant === 'no_invite') throw new InviteCheckError(INVITE_EXPIRED);
}
